import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../firebase';

const INVITES_STORAGE_KEY = 'driver_invites_v1';
const INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function generateToken() {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function readLocalInvites() {
  const raw = localStorage.getItem(INVITES_STORAGE_KEY);
  if (!raw) return [];

  try {
    return JSON.parse(raw);
  } catch (error) {
    console.error('Failed to parse local invites:', error);
    return [];
  }
}

function saveLocalInvites(invites) {
  localStorage.setItem(INVITES_STORAGE_KEY, JSON.stringify(invites));
}

export function buildInviteLink(token) {
  const base = window.location.origin;
  return `${base}/inspector#/accept-invite?token=${encodeURIComponent(token)}`;
}

export async function createDriverInvite({ email, driverName, invitedByUid, invitedByEmail }) {
  const normalizedEmail = (email || '').trim().toLowerCase();
  const token = generateToken();
  const createdAtMs = Date.now();
  const expiresAtMs = createdAtMs + INVITE_TTL_MS;

  if (!normalizedEmail) {
    throw new Error('Driver email is required to create an invite.');
  }

  const invite = {
    token,
    email: normalizedEmail,
    driverName: (driverName || '').trim(),
    role: 'driver',
    status: 'pending',
    createdAtMs,
    expiresAtMs,
    invitedByUid: invitedByUid || null,
    invitedByEmail: invitedByEmail || null
  };

  if (isFirebaseConfigured && db) {
    await addDoc(collection(db, 'driverInvites'), {
      ...invite,
      createdAt: serverTimestamp(),
      expiresAt: new Date(expiresAtMs)
    });
  } else {
    const invites = readLocalInvites();
    invites.unshift(invite);
    saveLocalInvites(invites);
  }

  return {
    invite,
    inviteLink: buildInviteLink(token)
  };
}

export async function getInviteByToken(token) {
  if (!token) {
    throw new Error('Invite token is missing.');
  }

  if (isFirebaseConfigured && db) {
    const q = query(collection(db, 'driverInvites'), where('token', '==', token));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const docSnap = snapshot.docs[0];
    return {
      id: docSnap.id,
      ...docSnap.data()
    };
  }

  const invites = readLocalInvites();
  return invites.find((item) => item.token === token) || null;
}

export async function markInviteAccepted({ token, acceptedByUid }) {
  if (!token) return;

  if (isFirebaseConfigured && db) {
    const q = query(collection(db, 'driverInvites'), where('token', '==', token));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return;

    const inviteDoc = snapshot.docs[0];
    await updateDoc(doc(db, 'driverInvites', inviteDoc.id), {
      status: 'accepted',
      acceptedByUid: acceptedByUid || null,
      acceptedAt: serverTimestamp()
    });
    return;
  }

  const invites = readLocalInvites();
  const updated = invites.map((item) => {
    if (item.token !== token) return item;
    return {
      ...item,
      status: 'accepted',
      acceptedByUid: acceptedByUid || null,
      acceptedAtMs: Date.now()
    };
  });
  saveLocalInvites(updated);
}

export function isInviteUsable(invite) {
  if (!invite) return false;
  if (invite.status && invite.status !== 'pending') return false;

  let expiresAtMs = invite.expiresAtMs || null;
  if (!expiresAtMs && invite.expiresAt?.toDate) {
    expiresAtMs = invite.expiresAt.toDate().getTime();
  }
  if (!expiresAtMs && invite.expiresAt?.seconds) {
    expiresAtMs = invite.expiresAt.seconds * 1000;
  }

  if (!expiresAtMs) return true;
  return Date.now() <= expiresAtMs;
}

export async function ensureUserProfile({ uid, email, displayName, role = 'driver' }) {
  if (!uid) return;

  if (isFirebaseConfigured && db) {
    const userRef = doc(db, 'users', uid);
    const existing = await getDoc(userRef);
    const companyId = existing.exists() && existing.data().companyId ? existing.data().companyId : uid;
    if (existing.exists()) {
      await setDoc(userRef, {
        email: email || existing.data().email || null,
        displayName: displayName || existing.data().displayName || null,
        role: existing.data().role || role,
        companyId: existing.data().companyId || companyId,
        updatedAt: serverTimestamp()
      }, { merge: true });
      return;
    }

    await setDoc(userRef, {
      email: email || null,
      displayName: displayName || null,
      role,
      companyId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
    return;
  }

  localStorage.setItem('user_role', role);
  localStorage.setItem('company_id', uid);
}

export async function getUserRole(uid) {
  const profile = await getUserProfile(uid);
  return profile.role || 'manager';
}

export async function getUserProfile(uid) {
  if (isFirebaseConfigured && db && uid) {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      const data = snap.data();
      return {
        uid,
        ...data,
        role: data.role || 'manager',
        companyId: data.companyId || uid
      };
    }

    return {
      uid,
      role: 'manager',
      companyId: uid
    };
  }

  const demoUserRaw = localStorage.getItem('demo_user');
  const demoUser = demoUserRaw ? JSON.parse(demoUserRaw) : null;

  return {
    uid: uid || demoUser?.uid || 'demo-user',
    email: demoUser?.email || null,
    displayName: demoUser?.displayName || null,
    role: localStorage.getItem('user_role') || demoUser?.role || 'manager',
    companyId: localStorage.getItem('company_id') || demoUser?.companyId || uid || 'demo-company'
  };
}